import { useCallback, useEffect, useMemo, useRef } from 'react'
import MaldivesDirectSearchFlightCalendar from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendar'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import type { DateRange } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/types'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useOfferCharterCalendarData'
import { fareClassToUrlMap, toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { parseAsBoolean, useQueryState } from 'nuqs'
import CharterBundleCalendarActions from './CharterBundleCalendarActions'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import MaldivesCharterCalendarBanner from 'components/Flights/CharterBundle/MaldivesCharterCalendarBanner'
import { useIntl } from 'react-intl'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { MaldivesCharterCalendarModalDesktop, MaldivesCharterCalendarModalMobile } from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesCharterCalendarModalLayout'

const CHARTER_BUNDLE_MODAL_TEST_ID = 'charter-bundle-calendar-modal'

interface Props {
  selectedMonth: string
  onMonthChange: (month: string) => void
  fareClass: FareClassWithReward
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  onFareClassChange?: (fareClass: FareClassWithReward) => void
  offer: App.HotelOffer
  duration: number
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  dateRangesByMonth: Record<string, Array<DateRange>>
  charterBundleRoute?: App.CharterBundleRoute
  isOpen: boolean
  onClose: () => void
  /** Callback when dates are selected */
  onDateSelect: (option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => void
  feederOriginAirportCode?: string
}

function CharterBundleCalendarModal(props: Props) {
  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()
  const {
    offer,
    duration,
    rooms,
    checkInDate,
    selectedMonth,
    onMonthChange,
    fareClass,
    priceMode,
    onDurationChange,
    durationOptions,
    onFareClassChange,
    dateRangesByMonth,
    charterBundleRoute,
    isOpen,
    onClose,
    onDateSelect,
    feederOriginAirportCode,
  } = props

  const fareClassForSearch = toBaseFareClass(fareClass)

  // Fetch charter calendar data inside the modal so it responds to updates
  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms,
    fareClass: fareClassForSearch,
    dateRangesByMonth,
    charterBundleRoute,
    feederOriginAirportCode,
  })

  const modalBodyRef = useRef<HTMLDivElement | null>(null)
  const isDesktop = useScreenSizeUp('desktop')

  // Read luxPlus state from URL (same as parent component)
  // Default to false if brand is not Luxury Escapes
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(config.BRAND === LUXURY_ESCAPES),
  )

  // Persist the user's confirmed fare-class pick (after they select a date in the carousel) to
  // the URL so reward selections survive page reloads. The dropdown filter above the calendar
  // does NOT call this — it only updates modal-local filter state (see `cabinFilter` below).
  const { fareClassFromUrl, setFareClassUrl, cabinFilter, setCabinFilter, rewardSeatsOnly, setRewardSeatsOnly, infiniteMemberOnly, setInfiniteMemberOnly } = useCharterFilterUrlState()
  const setFareClassWithUrl = useCallback((fc: FareClassWithReward | null) => {
    if (fc) {
      onFareClassChange?.(fc)
      setFareClassUrl(fareClassToUrlMap[fc])
    }
  }, [onFareClassChange, setFareClassUrl])

  // Filter durationOptions to only include those with matching packages
  const filteredDurationOptions = useMemo(() => {
    return durationOptions.filter(option => {
      // Check if offer has a package with matching duration
      return offer.packages.some(pkg => pkg.duration === option.value)
    })
  }, [durationOptions, offer.packages])
  const charterFareClassPolicy = useCharterFareClassPolicy(offer, rooms)
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)

  // If current duration doesn't have a matching package, switch to first available duration
  useEffect(() => {
    const hasMatchingPackage = offer.packages.some(pkg => pkg.duration === duration)
    if (!hasMatchingPackage && filteredDurationOptions.length > 0) {
      const firstAvailableDuration = filteredDurationOptions[0].value
      if (firstAvailableDuration !== duration) {
        onDurationChange(firstAvailableDuration)
      }
    }
  }, [duration, offer.packages, filteredDurationOptions, onDurationChange])

  // Calculate selected option ID based on current checkIn date and duration
  const selectedOptionId = useMemo(() => {
    if (!checkInDate) return undefined
    return `${checkInDate}-${duration}n`
  }, [checkInDate, duration])

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string, fareClass?: FareClassWithReward }) => {
    const selectedCabin = option.fareClass ?? fareClass
    if (selectedCabin) {
      setFareClassWithUrl(selectedCabin)
    }
    onDateSelect(option)
  }, [onDateSelect, fareClass, setFareClassWithUrl])

  const handlePriceModeChange = useCallback((nextPriceMode: 'total' | 'perPerson') => {
    offerPageDispatch({ type: OfferPageActions.setPriceMode, priceMode: nextPriceMode })
  }, [offerPageDispatch])

  const calendarProps = {
    onDateSelect: handleDateSelect,
    nights: duration,
    occupancies: rooms,
    luxPlusEnabled,
    itemBundleDataByDate: flightCalendarData.itemBundleDataByDate,
    dateRangesByMonth,
    selectedMonth,
    onMonthChange,
    fareClass: fareClassForSearch,
    selectedFareClass: fareClassFromUrl ?? fareClass,
    cabinFilter,
    priceMode,
    selectedOptionId,
    mode: 'offer' as const,
    offer,
    feederOriginAirportCode,
    rewardSeatsOnly,
    infiniteMemberOnly,
  }

  const baseActionsProps = {
    nights: duration,
    onDurationChange,
    durationOptions: filteredDurationOptions,
    luxPlusEnabled,
    onLuxPlusToggle: setLuxPlusEnabled,
    cabinFilter,
    onCabinFilterChange: setCabinFilter,
    availableFareClasses: allowedFareClassesForModal,
    rewardSeatsOnly,
    onRewardSeatsOnlyChange: setRewardSeatsOnly,
    infiniteMemberOnly,
    onInfiniteMemberOnlyChange: setInfiniteMemberOnly,
  }

  const desktopActionsElement = (
    <CharterBundleCalendarActions
      {...baseActionsProps}
    />
  )
  const mobileActionsElement = (
    <CharterBundleCalendarActions
      {...baseActionsProps}
      priceMode={priceMode}
      onPriceModeChange={handlePriceModeChange}
    />
  )
  const selectTripDatesLabel = intl.formatMessage({
    id: '8y9vgMTYZTXakoijFb6D',
    defaultMessage: 'Select your trip dates',
    description: 'Title for the charter bundle calendar modal in the trip planner',
  })

  if (isDesktop) {
    return (
      <MaldivesCharterCalendarModalDesktop
        isOpen={isOpen}
        ref={modalBodyRef}
        title={selectTripDatesLabel}
        headerExtension={desktopActionsElement}
        size="max"
        isPrimaryActionHidden
        flushListItems
        onClose={onClose}
        data-testid={CHARTER_BUNDLE_MODAL_TEST_ID}
        $testId={CHARTER_BUNDLE_MODAL_TEST_ID}
      >
        {isOpen && <MaldivesCharterCalendarBanner rooms={rooms} duration={duration} charterBundleRoute={charterBundleRoute} />}
        {isOpen && <MaldivesDirectSearchFlightCalendar
          {...calendarProps}
          onPriceModeChange={handlePriceModeChange}
          onInfiniteMemberOnlyChange={setInfiniteMemberOnly}
        />}
      </MaldivesCharterCalendarModalDesktop>
    )
  }

  return (
    <MaldivesCharterCalendarModalMobile
      isOpen={isOpen}
      ref={modalBodyRef}
      title={selectTripDatesLabel}
      height="full"
      isPrimaryActionHidden
      flushListItems
      onClose={onClose}
      data-testid={CHARTER_BUNDLE_MODAL_TEST_ID}
    >
      {isOpen && <MaldivesDirectSearchFlightCalendarMobile
        {...calendarProps}
        headerActions={mobileActionsElement}
        stickyHeaderBanner={<MaldivesCharterCalendarBanner rooms={rooms} duration={duration} charterBundleRoute={charterBundleRoute} flush />}
      />}
    </MaldivesCharterCalendarModalMobile>
  )
}

export default withOfferPageAndReduxState((state) => ({
  offer: state.offer! as App.HotelOffer,
  duration: state.duration,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  fareClass: state.fareClass,
  priceMode: state.priceMode,
  charterBundleRoute: state.charterBundleRoute,
  feederOriginAirportCode: state.airport?.code,
}), CharterBundleCalendarModal)
